import { SimpleInterpolation, StyledProps } from 'styled-components';

export interface ModifierObjValue {
  styles: SimpleInterpolation;
}

export type ModifierConfigValue = <TProps extends {} = {}>(props: Props<TProps>) => SimpleInterpolation;

/**
 * Function to help type modifiers config.
 * If using ts 5 then styled component props gets typed correctly.
 */
export const decorateModifiersConfig = <TMap = ModifiersConfig, TKeys extends string = Extract<keyof TMap, string>>(
  config: TMap
) => {
  return config as unknown as ModifiersConfig<TKeys>;
};
/**
 * An object declaring modifiers for use within a component.
 */
export type ModifiersConfig<TKeys extends string = string> = Record<TKeys, ModifierConfigValue>;

export type ComponentProps<TProps extends {} = {}> = StyledProps<TProps>;

type ModifiersProp<TMap extends ModifiersConfig> = keyof TMap | (keyof TMap)[];

type Props<TProps extends {} = {}> = { modifiers?: ModifiersProp<ModifiersConfig> } & ComponentProps<TProps>;

const emptyArray = [];
export const applyStyleModifiers = (modifiersConfig: ModifiersConfig) => {
  return (componentProps: Props) => {
    const modifiers = componentProps.modifiers ?? emptyArray;
    const normalizedModifiers: string[] = Array.isArray(modifiers) ? modifiers : [modifiers];

    const styles = normalizedModifiers
      .reduce((acc: SimpleInterpolation[], modifierName) => {
        const modifierConfigValue = modifiersConfig[modifierName];

        if (typeof modifierConfigValue === 'function') {
          const styles = modifierConfigValue(componentProps);
          const formattedStyles = Array.isArray(styles) ? styles.join('') : styles;
          acc.push(formattedStyles);
        }

        return acc;
      }, [])
      .join(' ');

    return styles;
  };
};
